import React from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Search, AlertTriangle, Clock, CheckCircle2, Loader2 } from "lucide-react";

const STATUS_META = {
  high_risk:  { icon: AlertTriangle, bg_bg: "bg-rose-100",    bg_dark: "bg-rose-500/15",   text: "text-rose-600 dark:text-rose-300", label_bg: "Висок риск",  label_en: "High Risk" },
  incomplete: { icon: Clock,         bg_bg: "bg-amber-100",   bg_dark: "bg-amber-500/15",  text: "text-amber-600 dark:text-amber-300", label_bg: "Непълни",      label_en: "Incomplete" },
  stable:     { icon: CheckCircle2,  bg_bg: "bg-emerald-100", bg_dark: "bg-emerald-500/15",text: "text-emerald-600 dark:text-emerald-300", label_bg: "Стабилен",  label_en: "Stable" },
};

function timeAgo(iso, bg) {
  if (!iso) return bg ? "никога" : "never";
  try {
    const then = new Date(iso);
    const mins = Math.floor((Date.now() - then.getTime()) / 60000);
    if (mins < 60)  return bg ? `преди ${mins} мин` : `${mins}m ago`;
    if (mins < 1440) return bg ? `преди ${Math.floor(mins / 60)} ч` : `${Math.floor(mins / 60)}h ago`;
    const days = Math.floor(mins / 1440);
    if (days === 0) return bg ? "Днес" : "Today";
    if (days === 1) return bg ? "Вчера" : "Yesterday";
    return bg ? `преди ${days} дни` : `${days}d ago`;
  } catch { return iso; }
}

export default function PatientList({ patients, loading, selectedId, onSelect, search, onSearch }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  return (
    <div className="flex flex-col h-full" data-testid="patient-list">
      {/* Search */}
      <div className={`p-4 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
        <div className={`relative rounded-lg border
          ${isDark ? "bg-[hsl(222,47%,9%)] border-white/10" : "bg-gray-50 border-gray-200"}`}>
          <Search className={`w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={bg ? "Търсене на пациенти..." : "Search patients..."}
            className={`w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-transparent outline-none
              ${isDark ? "text-white placeholder:text-gray-500" : "text-gray-900 placeholder:text-gray-400"}`}
            data-testid="patient-search"
          />
        </div>
        <div className={`mt-2 text-[11px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {bg ? `${patients.length} пациенти` : `${patients.length} patients`}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
          </div>
        ) : patients.length === 0 ? (
          <div className={`text-center py-8 text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {bg ? "Няма пациенти" : "No patients"}
          </div>
        ) : patients.map(p => {
          const meta = STATUS_META[p.status] || STATUS_META.stable;
          const StatusIcon = meta.icon;
          const active = p.id === selectedId;
          const initials = (p.name || p.email || "?").slice(0, 2).toUpperCase();

          return (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={`w-full text-left px-4 py-3 border-b transition-colors
                ${active
                  ? (isDark ? "bg-violet-500/10 border-white/5" : "bg-violet-50 border-gray-100")
                  : (isDark ? "border-white/5 hover:bg-white/[0.02]" : "border-gray-100 hover:bg-gray-50")}`}
              data-testid={`patient-row-${p.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0
                  bg-gradient-to-br from-violet-500 to-fuchsia-600">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className={`text-sm font-semibold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {p.name || p.email}
                    </h3>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 flex-shrink-0
                      ${isDark ? meta.bg_dark : meta.bg_bg} ${meta.text}`}>
                      <StatusIcon className="w-2.5 h-2.5" />
                      {bg ? meta.label_bg : meta.label_en}
                    </span>
                  </div>
                  <div className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {p.age ? `${p.age} ${bg ? "г." : "y"}` : "—"}
                    {p.gender && ` · ${p.gender === "male" ? (bg ? "мъж" : "M") : p.gender === "female" ? (bg ? "жена" : "F") : ""}`}
                  </div>
                  <div className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {bg ? "Последна активност: " : "Last update: "}{timeAgo(p.last_update, bg)}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
